import { faClock, faRandom } from '@fortawesome/sharp-regular-svg-icons';
import {
  faCreditCard,
  faFileImport,
  faGear,
  faHome,
  faLockAlt,
  faSensorAlert,
  faSignOut,
  faSlidersH,
  faTable,
  faTimelineArrow,
  faUserCog,
} from '@fortawesome/sharp-solid-svg-icons';
import { SCToast } from '@lib/sc-eone-ui';
import { Button } from 'eone-ui/buttons/button';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import { FontAwesome, Icon } from 'eone-ui/icons/icons';
import { Link } from 'react-router-dom';
import useResizeObserver from 'use-resize-observer';
import { usePermissions } from '../../../hooks/usePermissions';
import './menu.css';

function MenuItemLogout(props) {
  const logout = async () => {
    const response = await fetch('/home/logout');
    const responseJson = JSON.parse(await response.text());
    if (response.ok) window.location.href = responseJson.data;
    else {
      SCToast.error(responseJson.data);
    }
  };

  return (
    <div className="pd-menu-item">
      <Button OnClick={logout} Icon={faSignOut} Label={'Sign out'} DataTestId={props.DataTestId} />
    </div>
  );
}

function MenuSeparator() {
  return (
    <div className="pd-menu-separator">
      <div className="pd-menu-separator-line" />
    </div>
  );
}

function MenuItem(props) {
  if (!props.Visible && !props.Disabled) {
    return null;
  }

  if (props.Label === '-') {
    return <MenuSeparator />;
  }
  if (props.Disabled) {
    return (
      <div className="pd-menu-item-container" data-testid={props.DataTestId}>
        <div
          className="pd-menu-item-disabled"
          title={
            props.DisabledMessage ||
            'This feature requires a subscription that your account does not have enabled.'
          }
        >
          <FontAwesome ClassName={'pd-menu-item-icon'} Icon={props.Icon} FixedWidth />
          <span className="pd-menu-item-label">{props.Label}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pd-menu-item-container" data-testid={props.DataTestId}>
      <Link className="pd-menu-item" to={props.MenuLink} accessKey={props.AccessKey}>
        <Icon ClassName={'pd-menu-item-icon'} Icon={props.Icon} Type={props.IconType} FixedWidth />
        <span className="pd-menu-item-label">{props.Label}</span>
        {props.Badge && <span className="pd-menu-item-badge">{props.Badge}</span>}
      </Link>
    </div>
  );
}

function Menu(props) {
  const { ref } = useResizeObserver();
  const { canAtLeast } = usePermissions();
  const canViewErrorProcessing = canAtLeast('Integration Errors', 'Reader');
  const canImport = canAtLeast('Import-Export/Import', 'Contributor');

  function getMItems() {
    return [
      {
        Label: 'Home',
        Icon: faHome,
        Link: '/dashboard',
        Visible: true,
        TestId: 'menu_home',
      },
      {
        Label: 'Connections',
        Icon: faRandom,
        Link: '/connectors',
        Visible: true,
        TestId: 'menu_connectors',
      },
      {
        Label: 'Data sources',
        Icon: faTable,
        Link: '/data_source',
        Visible: true,
        TestId: 'menu_dataSources',
      },
      {
        Label: 'Integrations',
        Icon: faTimelineArrow,
        Link: '/integrations',
        Visible: true,
        TestId: 'menu_processes',
      },
      {
        Label: 'Error processing',
        Icon: faSensorAlert,
        Link: '/integration_errors',
        Visible: canViewErrorProcessing,
        TestId: 'menu_errorProcessing',
      },
      {
        Label: 'Scheduling',
        Icon: faClock,
        Link: '/scheduler',
        Visible: true,
        TestId: 'menu_scheduling',
      },
      {
        Label: '-',
        Visible: true,
        Disabled: false,
      },
      {
        Label: 'Setup',
        Icon: faSlidersH,
        Link: '/setup',
        Visible: true,
        TestId: 'menu_setup',
      },
      {
        Label: 'Security',
        Icon: faLockAlt,
        Link: '/security',
        Visible: true,
        TestId: 'menu_security',
      },
      {
        Label: 'System',
        Icon: faGear,
        Link: '/system',
        Visible: true,
        TestId: 'menu_system',
      },
      {
        Label: '-',
        Visible: canImport,
        Disabled: false,
      },
      //{
      //  Label: 'App store',
      //  Icon: faStore,
      //  Link: '/app_store',
      //  AccessKey: 'h',
      //  TestId: 'menu_appStore',
      //},
      {
        Label: 'Import',
        Icon: faFileImport,
        Link: '/import',
        Visible: canImport,
        TestId: 'menu_import',
      },
      // {
      //   Label: 'Multi import',
      //   Icon: faFileImport,
      //   Link: '/multiImport',
      //   Visible: true,
      //   TestId: 'menu_multiImport',
      // },
      //{
      //  Label: 'Export',
      //  Icon: faFileExport,
      //  Link: '/export',
      //  TestId: 'menu_export',
      //},
      {
        Label: '-',
        Visible: true,
        Disabled: false,
      },
      {
        Label: 'Account',
        Icon: faCreditCard,
        Link: '/account',
        Visible: true,
        TestId: 'menu_account',
      },
      {
        Label: 'Edit profile',
        Icon: faUserCog,
        Link: '/profile',
        Visible: true,
        TestId: 'menu_profile',
      },
    ];
  }

  const getMenuItems = () => {
    const menuItems = getMItems();
    return menuItems.map((item, index) => {
      return (
        <MenuItem
          key={index}
          Label={item.Label}
          Icon={item.Icon}
          //IconType={item.IconType}
          //AccessKey={props.Open ? item.AccessKey : undefined}
          MenuLink={item.Link}
          Visible={item.Visible}
          Disabled={item.Disabled}
          //DisabledMessage={item.DisabledMessage}
          DataTestId={getDataTestId('main-menu', item.Label)}
        />
      );
    });
  };
  if (!props.Visible) {
    return null;
  }
  return (
    <div className="pd-menu-panel">
      <div className="pd-menu-body">
        <div ref={ref} id="menu-items" className="pd-menu-items">
          {getMenuItems()}
          <MenuItemLogout DataTestId="main-menu.logout" />
        </div>
      </div>
      {/*<MenuFooter DataTestId="main-menu.footer" />*/}
    </div>
  );
}

export { Menu, MenuSeparator };
